// The student's class page, its parts (byj-1wv.51.6): what the instructor
// may do and the consent, folded under the head in the very words the join
// page showed — read when joining, not every visit, yet where a student
// finds what applies now, the instructor free to change the policy after
// they joined — and each open assignment a card: how far the student has
// got as a badge and a stripe, when it is due as a pill counting the days,
// what to do, its share command, and their shares to it and recordings of
// it as chips, each the way to it.
import { useRef, useState } from "react";
import type { Assignment, ClassPolicy, Consent, Recording, ShareFile, ShareSummary } from "../api";
import { sessionLabel } from "../classroom";
import { Link } from "./Nav";
import { Refused, refusalOf, type Refusal } from "./Refused";
import { kindIcon, kindOf } from "../kinds";
import { t, whenMinute, word, phrases } from "../i18n";
import { reviewWords } from "../files";
import { FoldPanel } from "./SidePanel";
import { ConsentText, PolicyWords, ShareCommand } from "./JoinView";

const Day = 24 * 3_600_000;

/** What the instructor may do and the consent, folded as the share's side panel folds a section: on opening, the join page's own words for both. */
export function PolicyBar({ policy, consent }: { policy: ClassPolicy; consent: Consent }) {
  return (
    <FoldPanel label={t("classes:policyTitle")} summary={<span className="muted small">{t("classes:consentVersion", { v: consent.version })}</span>} className="card class-policy-fold">
      <PolicyWords policy={policy} />
      <ConsentText consent={consent} />
    </FoldPanel>
  );
}

/** How far a student has got with an assignment: nothing yet, a share to it, a recording of it. */
type Progress = "todo" | "doing" | "recorded";

/**
 * A time's calendar day in the viewer's zone, as a count of days: its date
 * read in the zone, counted as UTC's, so a zone's daylight saving — an hour
 * either way, far from UTC as it may be — never makes a day of 23 or 25
 * hours round to the wrong one.
 */
const dayOf = (ms: number) => {
  const d = new Date(ms);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / Day;
};

/**
 * When an assignment is due, against now, counted in calendar days as a
 * person counts them — "today" is today's date, not the next 24 hours, and
 * tomorrow is "tomorrow", an hour or a day and an hour away: passed (today,
 * or so many days ago), today or tomorrow, or so many days left.
 */
export function dueState(a: Assignment, now: number): { tone: "over" | "soon" | "later"; words: string } | null {
  if (!a.due_at) return null;
  const due = Date.parse(a.due_at);
  const days = dayOf(due) - dayOf(now);
  if (due < now) return { tone: "over", words: days === 0 ? t("classes:duePassedToday") : t("classes:dueOverBy", { count: -days }) };
  if (days <= 1) return { tone: "soon", words: days === 0 ? t("classes:dueToday") : t("classes:dueTomorrow") };
  return { tone: "later", words: t("classes:dueInDays", { count: days }) };
}

/**
 * The open assignments, a card each — its progress, its due date, what to
 * do, its share command and the student's own work on it — then the command
 * for a share of no assignment.
 */
/**
 * Handing a file in under an assignment (RFC-054 §5): one chosen, put as a
 * file of the student's handed in under it; or the line that does it from a
 * terminal, which puts a folder too.
 */
function HandIn({ code, assignment, handIn }: { code: string; assignment: string; handIn: (f: File, assignment: string) => Promise<void> }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<Refusal | null>(null);
  const put = async (f: File | undefined) => {
    if (!f) return;
    setBusy(f.name);
    setError(null);
    setRefused(null);
    try {
      await handIn(f, `${code}/${assignment}`);
    } catch (err) {
      const r = refusalOf(err, f.size);
      if (r) setRefused(r);
      else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div className="row wrap hand-in" data-testid="hand-in">
      <button className="small" disabled={busy !== null} onClick={() => input.current?.click()}>
        {busy ? t("files:handingIn", { name: busy }) : t("files:handIn")}
      </button>
      <input ref={input} type="file" hidden data-testid={`hand-in-${assignment}`} onChange={(e) => void put(e.target.files?.[0])} />
      <code className="muted small">{t("files:handInByTerminal", { code, key: assignment })}</code>
      {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      {refused && <Refused r={refused} />}
    </div>
  );
}

export function AssignmentCards({ code, assignments, shares, recordings, now, files = [], handIn }: {
  code: string;
  assignments: Assignment[];
  shares: ShareSummary[];
  recordings: Recording[];
  /** the page's clock (now.ts), so how a due date stands moves while it is open */
  now: number;
  /** what the student handed in under the class's assignments (RFC-054 §5) */
  files?: ShareFile[];
  /** hands a file in under an assignment; none, and none is handed in here (the instructor's preview) */
  handIn?: (f: File, assignment: string) => Promise<void>;
}) {
  if (assignments.length === 0) {
    return (
      <section data-testid="assignment-commands">
        <p>{t("classes:studentClassLead")}</p>
        <ShareCommand code={code} />
      </section>
    );
  }
  return (
    <section data-testid="assignment-commands">
      <ul className="student-assignments">
        {assignments.map((a) => {
          const sh = shares.filter((s) => s.assignment?.key === a.key);
          const rs = recordings.filter((r) => r.assignment?.key === a.key);
          const fs = files.filter((f) => f.hand_in?.key === a.key);
          const progress: Progress = rs.length > 0 ? "recorded" : sh.length > 0 || fs.length > 0 ? "doing" : "todo";
          const due = dueState(a, now);
          return (
            <li key={a.key} className={`card student-assignment is-${progress}`} data-testid={`assignment-${a.key}`}>
              <div className="student-assignment-head">
                <h3 className="student-assignment-title">{a.title}</h3>
                <code className="student-assignment-key">{a.key}</code>
                <span className="grow" />
                {due && (
                  // the time as well as the day, said and not a title away: a touch screen has no hover
                  <span className={`chip${due.tone === "later" ? "" : " chip-warn"}`}>
                    {t("classes:dueOn", { at: whenMinute(a.due_at!) })} · {due.words}
                  </span>
                )}
                <span className={`chip${progress === "doing" ? " chip-on" : progress === "recorded" ? " chip-ok" : ""}`} data-testid="progress">
                  {word("classes:progress", progress)}
                </span>
              </div>
              {a.text && <p className="student-assignment-text pre-wrap">{a.text}</p>}
              <ShareCommand code={code} assignment={a.key} />
              {handIn && <HandIn code={code} assignment={a.key} handIn={handIn} />}
              {(sh.length > 0 || rs.length > 0 || fs.length > 0) && (
                <ul className="work-chips" aria-label={t("classes:yourWorkOn", { title: a.title })} data-testid="yours">
                  {sh.map((s) => (
                    <li key={s.id}>
                      <Link to={`/s/${encodeURIComponent(s.id)}`} className={`work-chip${s.live ? " is-live" : ""}`}>
                        <span className="work-chip-icon" aria-hidden="true">
                          {kindIcon[kindOf(s.source)]}
                        </span>
                        {sessionLabel(s.title, s.id, s.started_at, new Date(now)).text}
                        <span className="work-chip-sub">{s.live ? t("shares:live") : t("shares:ended")}</span>
                      </Link>
                    </li>
                  ))}
                  {rs.map((r) => (
                    <li key={r.id}>
                      <Link to={`/r/${encodeURIComponent(r.id)}`} className="work-chip is-recording">
                        <span className="work-chip-icon" aria-hidden="true">
                          ●
                        </span>
                        {r.title || r.id}
                        <span className="work-chip-sub">
                          {phrases.recordingTurns(r.turns[0], r.turns[1])}
                          {r.screen && ` · ${t("shares:screenKept")}`}
                        </span>
                      </Link>
                    </li>
                  ))}
                  {fs.map((f) => (
                    <li key={f.id}>
                      <Link to={`/f/${encodeURIComponent(f.id)}`} className="work-chip is-file">
                        <span className="work-chip-icon" aria-hidden="true">
                          ▤
                        </span>
                        {f.name}
                        <span className="work-chip-sub">
                          {f.latest ? t("files:fileVersion", { n: f.latest }) : t("files:fileNoVersion")}
                          {/* its instructor's check, where the student looks first (byj-1wv.136) */}
                          {f.review && ` · ${reviewWords(f.review, f.latest)}`}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <div className="student-no-assignment">
        <p className="muted small flush">{t("classes:shareWithoutAssignment")}</p>
        <ShareCommand code={code} />
      </div>
    </section>
  );
}
